import { authenticatedFetch } from '../lib/api';

export type SupportSessionStatus =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'connected'
  | 'disconnected'
  | 'expired';

export type SupportMode = 'view' | 'control';

export interface SupportUserSearchResult {
  userId: string;
  email: string;
  fullName: string;
  organizationId: string;
  organizationCode: string;
  organizationName: string;
  portalStatus: 'active' | 'inactive';
  isOnline: boolean;
  lastActive?: string;
  lastLogin?: string;
  device?: string;
  browser?: string;
  ip?: string;
  sessionStatus?: SupportSessionStatus | null;
  role?: string;
  roleName?: string;
}

export interface SupportSession {
  sessionId: string;
  status: SupportSessionStatus;
  mode: SupportMode;
  engineerId: string;
  engineerName: string;
  engineerEmail: string;
  customerUserId: string;
  customerName: string;
  customerEmail: string;
  organizationId: string;
  organizationName: string;
  expiresAt: string;
  connectedAt?: string;
  disconnectedAt?: string;
  reason?: string;
  sessionToken?: string;
  chatSessionId?: string;
  purpose?: string;
  estimatedDurationMinutes?: number;
  permissions?: { viewScreen: boolean; controlPortal: boolean };
  guacamole?: { enabled: boolean; url?: string; connectionId?: string };
}

async function parseResponse<T>(res: Response): Promise<T> {
  const data = await res.json();
  if (!res.ok || !data.success) {
    throw new Error(data.error || `Request failed (${res.status})`);
  }
  return data.data as T;
}

export async function searchSupportUser(organizationCode: string, userId: string): Promise<SupportUserSearchResult> {
  const res = await authenticatedFetch('/api/v1/support/search-user', {
    method: 'POST',
    body: JSON.stringify({ organizationCode, userId }),
  });
  return parseResponse<SupportUserSearchResult>(res);
}

export async function requestSupportSessionFromChat(payload: {
  chatSessionId: string;
  mode?: SupportMode;
  purpose?: string;
  reason?: string;
  estimatedDurationMinutes?: number;
}): Promise<SupportSession> {
  const res = await authenticatedFetch('/api/v1/support/request-from-chat', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return parseResponse<SupportSession>(res);
}

export async function getChatRemoteStatus(chatSessionId: string): Promise<{
  chatSessionId: string;
  customerUserId: string;
  customerOnline: boolean;
  activeRemoteSessionId: string | null;
  remoteSessionStatus: string | null;
  canRequestRemote: boolean;
}> {
  const res = await authenticatedFetch(`/api/v1/support/chat/${chatSessionId}/remote-status`);
  return parseResponse(res);
}

export async function requestSupportSession(payload: {
  customerUserId: string;
  mode?: SupportMode;
  reason?: string;
}): Promise<SupportSession> {
  const res = await authenticatedFetch('/api/v1/support/request-session', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return parseResponse<SupportSession>(res);
}

export async function getSupportSession(sessionId: string): Promise<SupportSession> {
  const res = await authenticatedFetch(`/api/v1/support/session/${sessionId}`);
  return parseResponse<SupportSession>(res);
}

export async function connectSupportSession(sessionId: string, sessionToken?: string): Promise<SupportSession> {
  const res = await authenticatedFetch(`/api/v1/support/session/${sessionId}/connect`, {
    method: 'POST',
    body: JSON.stringify({ sessionToken }),
  });
  return parseResponse<SupportSession>(res);
}

export async function disconnectSupportSession(sessionId: string, reason?: string): Promise<SupportSession> {
  const res = await authenticatedFetch('/api/v1/support/disconnect', {
    method: 'POST',
    body: JSON.stringify({ sessionId, reason }),
  });
  return parseResponse<SupportSession>(res);
}

export async function getGuacamoleConfig(sessionId: string): Promise<{ enabled: boolean; url?: string; connectionId?: string }> {
  const res = await authenticatedFetch(`/api/v1/support/session/${sessionId}/guacamole`);
  return parseResponse(res);
}
